import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play, MapPin, Sparkles } from 'lucide-react';
import ProceduralGroundBackground from './ui/procedural-ground-background';

import HeroCanvas3D from './HeroCanvas3D';

import './Hero.css';
import technoSapiansImg from '../assets/TechnoSapians_Cover.jpeg';
import orientationImg from '../assets/Orientation_cover.png';
import workshopImg from "../assets/workshop.jpg";
import recruitementImg from "../assets/recruitement.jpg";
import educationalImg from "../assets/educational.webp";

const Hero = ({ onPageChange }) => {
  const [currentEventIndex, setCurrentEventIndex] = useState(0);

  // Event Data matching UpcomingEvents logic
  const events = [
    {
      title: "Educational Visit",
      location: "Offline",
      status: "upcoming",
      tag: "2026",
      subTag: "COMING SOON",
      link: "events",
      imageUrl: educationalImg
    },
    {
      title: "WORKSHOP",
      location: "Offline",
      status: "upcoming",
      tag: "2026",
      subTag: "AUDITORIUM",
      link: "events",
      imageUrl: workshopImg
    },
    {
      title: "Techno Sapians 2k27",
      location: "Offline",
      status: "closed",
      tag: "UPCOMING",
      subTag: "COMING SOON",
      link: "events",
      imageUrl: technoSapiansImg
    }
  ];

  // Rotate events
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentEventIndex((prev) => (prev + 1) % events.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="hero">
      {/* Layer 1: Background */}
      <div className="hero-background">
        <ProceduralGroundBackground />
        <div className="particle-system">
          {/* Particles handled by CSS opacity or future canvas impl */}
        </div>
      </div>

      <div className="container">
        <div className="hero-layout">

          {/* 3D VISUAL — first in DOM so on mobile (column) it appears on top */}
          <motion.div
            className="hero-3d-visual"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
          >
            <HeroCanvas3D />
          </motion.div>

          {/* TEXT GROUP — second in DOM, appears below 3D on mobile */}
          <div className="hero-main-group">
            {/* Layer 2: Main Content Top (Badge + Heading + Subtitle) */}
            <div className="hero-text-top">
              <motion.div
                className="hero-batch-badge"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Sparkles size={14} />
                <span>AI Tech Society · IITM</span>
              </motion.div>

              <motion.h1
                className="hero-title-large"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                AIEC <span className="highlight">IITM</span>
              </motion.h1>

              <motion.p
                className="hero-subtitle-large"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                Elevate your potential with Artificial Intelligence. We forge the bridge
                between human ingenuity and tomorrow's technology to shape world-class innovators.
              </motion.p>
            </div>

            {/* Layer 2b: Action buttons + Event Card */}
            <div className="hero-text-bottom">
              <motion.div
                className="hero-actions-row"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <button onClick={() => onPageChange('careers')} className="btn-hero-primary">
                  Join the Society <ArrowRight size={18} />
                </button>
                <button onClick={() => onPageChange('events')} className="btn-hero-secondary">
                  <Play size={18} fill="currentColor" /> Explore Events
                </button>
              </motion.div>

              {/* Dynamic Event Notification Card */}
              <div className="event-notification-wrapper">
                {events.map((event, index) => {
                  const offset = (index - currentEventIndex + events.length) % events.length;

                  let state = "deep";
                  if (offset === 0) state = "front";
                  else if (offset === 1) state = "back";

                  return (
                    <motion.div
                      key={index}
                      animate={state}
                      variants={{
                        front: {
                          zIndex: 30,
                          scale: 1,
                          opacity: 1,
                          y: 0,
                          x: 0,
                          rotateZ: 0,
                          filter: "blur(0px)",
                          transition: { type: "spring", stiffness: 300, damping: 25 }
                        },
                        back: {
                          zIndex: 20,
                          scale: 0.98,
                          opacity: 0.9,
                          y: -6,
                          x: 0,
                          rotateZ: 1.5,
                          filter: "blur(0.5px)",
                          transition: { type: "spring", stiffness: 300, damping: 25, delay: 0.1 }
                        },
                        deep: {
                          zIndex: 10,
                          scale: 0.92,
                          opacity: 0.8,
                          y: -16,
                          x: -4,
                          rotateZ: -1,
                          filter: "blur(1px)",
                          transition: {
                            zIndex: { times: [0, 0.5, 0.51, 1], values: [30, 30, 10, 10] },
                            x: { times: [0, 0.4, 1], values: [0, 250, -4] },
                            y: { times: [0, 0.4, 1], values: [0, 50, -16] },
                            opacity: { times: [0, 0.4, 0.5, 1], values: [1, 0, 0, 0.6] },
                            rotateZ: { times: [0, 0.4, 1], values: [0, 15, -1] },
                            duration: 0.8,
                            ease: "easeInOut"
                          }
                        }
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 25
                      }}
                      className={`event-focus-card ${state}`}
                      onClick={() => {
                        if (state !== "front") return;
                        onPageChange(event.link);
                      }}
                    >
                      <div className="event-horizontal-container" style={{ position: 'relative', zIndex: 10 }}>
                        <div className="event-main-info">
                          <div className="event-info-header">
                            <span className="event-tag-upcoming">{event.tag || 'UPCOMING'}</span>
                            <div className="event-tag">{event.subTag || 'COMING SOON'}</div>
                          </div>
                          <h3 className="event-title">
                            <span className="event-game-name">{event.title}</span>
                          </h3>
                          <div className="event-location-row">
                            <div className="event-location">
                              <MapPin size={14} /> {event.location}
                            </div>
                          </div>
                        </div>

                        {event.status === 'open' && (
                          <div className="event-actions-side">
                            <button className="btn-participate-now compact">
                              Participate <ArrowRight size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
